import RadiusCard from "@components/layout/RadiusCard";
import { YStack, XStack, Text, Button, Stack, Circle, Spinner } from "tamagui";
import GasImg from "@styles/images/gas.png";
import { getGasImageByKg } from "@styles/images/quickorder/gasImages";
import { colors } from "@styles/colors";
import { v2 } from "@screen/Home/pages/GasShop/skin";
import { RiAddLine, RiSubtractLine, RiErrorWarningLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import { textTruncate } from "@utils/func/textTool";
import { useOrder, Product } from "@utils/store/cart/useOrder";
import { addProductItem, updateProductItem, delProductItem } from "@components/product/API/handleProductAPI";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { useUser } from "@utils/store/user/useUser";
import { buildXContextHeader } from "@utils/api/headers";
import { useState } from "react";
import InlineLoading from "@components/loading/InlineLoading";
import { track } from "@lib/tracker";
import { formatGasPriceRange } from "@utils/func/gasPriceRange";
import { isGasSkinV2, SkinButton, v2 as skinV2Colors } from "../skin";

import { GasCylinder, Commodity } from "../products";

const NextButton =({ data, totalQty }: { data: GasCylinder | Commodity; totalQty: number }) => {
  const navigation = useNavigate();
  const { setChooseProduct, draftOrderId, syncDraft, setOrderState } = useOrder();
  const { cusJwtToken } = useCusAuth();
  const { chooseIdentity } = useUser();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isGas = "gas_type" in data;
  const skinV2 = isGasSkinV2();

  const handleAddCommodity = async () => {
    if (isSubmitting || !draftOrderId) return;
    setIsSubmitting(true); //-- 行內 loading；不開全域遮罩，加購一件不該把整頁蓋掉
    try {
      const commodityData = data as Commodity;
      const res = await addProductItem({
        xContext: buildXContextHeader(chooseIdentity) || "",
        jwtToken: cusJwtToken || "",
        type: "commodity",
        draftId: draftOrderId,
        body: {
          commodity_id: Number(commodityData.commodity_id),
          price_id: Number(commodityData.price_id),
          qty: 1,
        },
      });
      if (res?.state === "success") {
        if (res.data?.data?.draft) {
          syncDraft(res.data.data.draft);
        } else {
          setOrderState("loading");
        }
      }
    } catch (error) {
      console.error("handleAddCommodity error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 「加入」在兩版共用的動作
  const handlePress = () => {
    if (!isGas) {
      handleAddCommodity();
      return;
    }
    // 一律「新增一瓶」：帶入全新商品（不含 line_id），讓安裝位流程建立新的一筆；
    // 既有品項的調整由下方各品項列進入
    track("funnel_step", { step: "products", gas_type: (data as GasCylinder).gas_type, kilogram: (data as GasCylinder).kilogram });
    setChooseProduct(data as unknown as Product);
    navigation("/gasShop/installPosition");
  };

  return (
    <Stack position="relative">
      {skinV2 ? (
        /* V2：列內動作用 inline 變體（淡藍底藍字）。實心藍只保留給頁面底部唯一的主要 CTA。 */
        <SkinButton label="加入" variant="inline" icon={<RiAddLine size={15} color={skinV2Colors.primary} />} disabled={isSubmitting} onPress={handlePress} />
      ) : (
        <Button
          onPress={handlePress}
          size="$2"
          p={5}
          h={35}
          className="gasshop-btn-gradient"
          width={35}
          borderRadius={10}
          elevation={0}
          disabled={isSubmitting}>
          <RiAddLine size={20} color="#fff" />
        </Button>
      )}
      {totalQty > 0 && (
        <Circle
          size={skinV2 ? 20 : 18}
          backgroundColor={skinV2 ? colors.primary : colors.danger}
          borderWidth={skinV2 ? 2 : 0}
          borderColor="#fff"
          position="absolute"
          t={skinV2 ? -6 : -8}
          r={skinV2 ? -4 : -8}
          jc="center"
          ai="center"
          elevation={2}
          zIndex={1}>
          <Text color="white" fontSize={10} fow="700">
            {totalQty}
          </Text>
        </Circle>
      )}
    </Stack>
  );
};

/** "haze"：Skin V2 的淡霧頁首（釘住的商品列）。白字讀不到、純黑又太生硬，改用深藍墨與灰藍。 */
type ThemeType = "basic" | "light" | "haze";

/**
 * compact 切換時的過場：安裝位置頁的商品列釘在標題列裡，捲動越過門檻會由原尺寸換成
 * compact 尺寸（縮圖、字級、間距一起變）。直接換值會「啪」一下跳，故對這幾個屬性掛
 * transition，讓它平順地縮。標題列是 absolute，這裡的高度變化不會牽動捲動內容重排。
 */
const SIZE_EASE = "220ms cubic-bezier(0.4, 0, 0.2, 1)";
const fontEase = { transition: `font-size ${SIZE_EASE}` } as const;

interface ProNameProps {
  title: string;
  desc: string;
  theme?: ThemeType;
  compact?: boolean;
}

const ProName = ({ title, desc, theme = "basic", compact = false }: ProNameProps) => {
  const themeData: Record<ThemeType, { title: string; des: string }> = {
    basic: {
      title: "#000",
      des: colors.gray,
    },
    light: {
      title: "#fff",
      des: "#fff",
    },
    haze: {
      title: v2.hazeInk,
      des: v2.hazeSub,
    },
  };
  return (
    <>
      <Text
        fontWeight="800"
        fontSize={compact ? 14 : 16}
        letterSpacing={-0.2}
        color={themeData[theme].title}
        style={fontEase}>
        {title}
      </Text>
      {desc && (
        <Text color={themeData[theme].des} fontSize={compact ? 12 : 14} style={fontEase}>
          {textTruncate(desc, 30, "...")}
        </Text>
      )}
    </>
  );
};

const ProPrice = ({
  price,
  originalPrice,
  theme = "basic",
  loading = false,
  compact = false,
}: {
  price: string | number;
  originalPrice?: string | number;
  theme: ThemeType;
  loading?: boolean;
  compact?: boolean;
}) => {
  const themeData: Record<ThemeType, { currency: string; price: string }> = {
    basic: {
      currency: colors.gray,
      price: colors.primary,
    },
    light: {
      currency: "#fff",
      price: "#fff",
    },
    haze: {
      currency: v2.hazeSub,
      price: v2.hazeInk,
    },
  };
  // 優惠券折扣尚未由 preview 解析時顯示 loading，避免閃一下原價再跳折後價
  if (loading) {
    return (
      <YStack ai="flex-end" jc="center" flexShrink={0} minWidth={56} height={44}>
        <Spinner size="small" color={themeData[theme].price} />
      </YStack>
    );
  }
  return (
    <YStack ai="flex-end" flexShrink={0}>
      {/* NT$ 退成小字灰藍、數字放大加粗：價格是這一列的第二主角，讓「數字」本身撐住，
          幣別只是註記。數字用 tabular-nums，換品項時位數變動不會左右跳動。 */}
      <Text color={themeData[theme].currency} fontSize={compact ? 11 : 12} fontWeight="600" style={fontEase}>
        NT$
        <Text
          fontSize={compact ? 17 : 22}
          fontWeight="800"
          color={themeData[theme].price}
          letterSpacing={-0.4}
          marginLeft={5}
          style={{ ...fontEase, fontVariantNumeric: "tabular-nums" }}>
          {price}
        </Text>
      </Text>
      {originalPrice != null && (
        <Text fontSize={12} color={themeData[theme].currency} style={{ textDecorationLine: "line-through" }}>
          NT$ {originalPrice}
        </Text>
      )}
    </YStack>
  );
};

export const GasProductItem = ({ data }: { data: GasCylinder | Commodity }) => {
  const isGas = "gas_type" in data;
  const isCommodity = "commodity_type" in data;
  const navigation = useNavigate();
  const { productList, setChooseProduct, draftOrderId, syncDraft, setOrderState, updateQuantity, removeProduct } = useOrder();
  // 參考售價區間：僅瓦斯瓶種有；是否顯示由 PW 後台開關決定，不看 hide_amount_on_pending
  const priceRangeText = isGas ? formatGasPriceRange((data as GasCylinder).price_range) : null;
  const { cusJwtToken } = useCusAuth();
  const { chooseIdentity } = useUser();
  const [updatingLineId, setUpdatingLineId] = useState<string | null>(null);

  const handleUpdateQuantity = async (p: Product, newQty: number) => {
    if (updatingLineId || !draftOrderId) return;

    //-- 樂觀更新: 先更新本地狀態
    if (newQty <= 0) {
      removeProduct(p.line_id!);
    } else {
      updateQuantity(p.line_id!, newQty);
    }

    setUpdatingLineId(p.line_id!); //-- 行內 loading；不開全域遮罩
    try {
      const type = isGas ? "gas" : "commodity";
      if (newQty <= 0) {
        const res = await delProductItem({
          xContext: buildXContextHeader(chooseIdentity) || "",
          jwtToken: cusJwtToken || "",
          type,
          draftId: draftOrderId,
          lineId: p.line_id!,
        });
        if (res?.state === "success") {
          if (res.data?.data?.draft) {
            syncDraft(res.data.data.draft);
          } else {
            setOrderState("loading");
          }
        }
      } else {
        const postData = { ...p };
        delete (postData as any).line_id;
        delete (postData as any).totalPrice;
        postData.qty = newQty;

        const res = await updateProductItem({
          xContext: buildXContextHeader(chooseIdentity) || "",
          jwtToken: cusJwtToken || "",
          type,
          draftId: draftOrderId,
          lineId: p.line_id!,
          body: postData,
        });
        if (res?.state === "success") {
          if (res.data?.data?.draft) {
            syncDraft(res.data.data.draft);
          } else {
            setOrderState("loading");
          }
        }
      }
    } catch (error) {
      console.error("handleUpdateQuantity error:", error);
      //-- 如果失敗，由於 syncDraft 會在後續被調用或頁面刷新，通常會恢復正確狀態
      //-- 這裡簡單處理，若有需要可以實作更嚴謹的 rollback
    } finally {
      setUpdatingLineId(null);
    }
  };

  const matchedProducts = productList.filter((p) => {
    if (isGas) {
      return Number(p.gas_id) === Number((data as GasCylinder).gas_id);
    }
    if (isCommodity) {
      return Number(p.price_id) === Number((data as Commodity).price_id);
    }
    return false;
  });

  const totalQty = matchedProducts.reduce((sum, p) => sum + p.qty, 0);

  const skinV2 = isGasSkinV2();

  const body = (
    <>
        <XStack justifyContent="space-between" alignItems="center">
          <div className="flex items-center gap-4 flex-1">
            {skinV2 ? (
              <YStack width={46} height={80} justifyContent="flex-end" alignItems="center" flexShrink={0}>
                <img
                  src={isGas ? getGasImageByKg((data as GasCylinder).kilogram) : GasImg}
                  alt="Gas"
                  style={{ width: 34, height: "auto", maxHeight: 80, objectFit: "contain" }}
                />
              </YStack>
            ) : (
              <YStack width={64} height={64} justifyContent="center" alignItems="center" flexShrink={0}>
                <img
                  src={isGas ? getGasImageByKg((data as GasCylinder).kilogram) : GasImg}
                  alt="Gas"
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              </YStack>
            )}
            <YStack flex={1}>
              {/* GAS 商品：訂購頁不顯示氣別，一律以「{公斤數}KG 瓦斯」呈現 */}
              {isGas && <ProName title={`${(data as GasCylinder).kilogram}KG 瓦斯`} desc="" theme="basic" />}
              {/* 其他商品 */}
              {isCommodity && <ProName title={(data as Commodity).display_name || ""} desc="" theme="basic" />}
              {/* 參考售價區間（顯示用，實際金額以瓦斯行報價為準） */}
              {priceRangeText && (
                <Text color={colors.gray} fontSize={13} mt={2}>
                  參考售價 {priceRangeText}
                </Text>
              )}
            </YStack>
          </div>
          <NextButton data={data} totalQty={totalQty} />
        </XStack>

        {/* 購物車中的細項 */}
        {matchedProducts.length > 0 && (
          <YStack
            mt={skinV2 ? "$2" : "$3"}
            gap="$2"
            borderTopWidth={skinV2 ? 0 : 1}
            borderTopColor={skinV2 ? undefined : colors.gray100}
            pt={skinV2 ? 0 : "$2"}>
            {matchedProducts.map((p) => (
              <XStack
                key={p.line_id}
                ai="center"
                jc="space-between"
                backgroundColor={skinV2 ? "#f8fafc" : undefined}
                borderRadius={skinV2 ? 14 : 0}
                px={skinV2 ? 12 : 0}
                py={skinV2 ? 9 : 0}>
                <XStack
                  ai="center"
                  gap="$2"
                  flex={1}
                  flexWrap="wrap"
                  onPress={() => {
                    if (isGas) {
                      setChooseProduct(p);
                      navigation("/gasShop/installPosition");
                    }
                  }}>
                  <Text color={colors.gray} fontSize={12} mr="$1">
                    安裝位置:
                  </Text>
                  {p.usage_location && p.floor != null ? (
                    <>
                      <XStack backgroundColor={colors.primaryActive} px={8} py={2} borderRadius={10} ai="center">
                        <Text color={colors.primary} fontSize={11} fontWeight="700">
                          {p.floor === 0 ? "1F" : p.floor > 0 ? `${p.floor}F` : `B${-p.floor}`}
                        </Text>
                      </XStack>
                      <XStack backgroundColor={colors.primaryActive} px={8} py={2} borderRadius={10} ai="center">
                        <Text color={colors.primary} fontSize={11} fontWeight="700">
                          {p.is_elevator ? "有電梯" : "無電梯"}
                        </Text>
                      </XStack>
                      <XStack backgroundColor={colors.primaryActive} px={8} py={2} borderRadius={10} ai="center">
                        <Text color={colors.primary} fontSize={11} fontWeight="700">
                          {p.usage_location}
                        </Text>
                      </XStack>
                    </>
                  ) : (
                    <XStack ai="center" gap="$1">
                      <RiErrorWarningLine color={colors.danger} size={14} />
                      <Text color={colors.danger} fontSize={12} fontWeight="700">
                        需要設定安裝位置
                      </Text>
                    </XStack>
                  )}
                </XStack>
                <XStack ai="center" gap="$3">
                  <Button
                    size="$1"
                    circular
                    bg={colors.light}
                    borderColor={colors.gray100}
                    borderWidth={1}
                    disabled={!!updatingLineId}
                    onPress={() => handleUpdateQuantity(p, p.qty - 1)}>
                    <RiSubtractLine size={14} color={colors.gray} />
                  </Button>
                  <XStack width={20} jc="center" ai="center" position="relative">
                    <Text fontWeight="bold" fontSize={16} textAlign="center" opacity={updatingLineId === p.line_id ? 0.3 : 1}>
                      {p.qty}
                    </Text>
                    {updatingLineId === p.line_id && (
                      <Stack position="absolute">
                        <InlineLoading />
                      </Stack>
                    )}
                  </XStack>
                  <Button
                    size="$1"
                    circular
                    bg={colors.light}
                    borderColor={colors.gray100}
                    borderWidth={1}
                    disabled={!!updatingLineId}
                    onPress={() => handleUpdateQuantity(p, p.qty + 1)}>
                    <RiAddLine size={14} color={colors.gray} />
                  </Button>
                </XStack>
              </XStack>
            ))}
          </YStack>
        )}
    </>
  );

  return <div style={{ width: "100%" }}>{skinV2 ? body : <RadiusCard>{body}</RadiusCard>}</div>;
};
export const GasProductTitle = ({
  title,
  desc,
  price = 0,
  originalPrice,
  theme = "light",
  tags = [],
  kilogram,
  priceLoading = false,
  hidePrice = false,
  compact = false,
  dense = false,
}: {
  title: string;
  desc: string;
  price: string | number;
  originalPrice?: string | number;
  theme?: ThemeType;
  tags?: string[];
  kilogram?: number;
  priceLoading?: boolean;
  /** 瓦斯行開啟隱藏金額（hide_amount_on_pending）時為 true → 不渲染價格 */
  hidePrice?: boolean;
  /** 精簡模式：標題列捲動收合時改用，縮圖與字級整體縮小，只矮不消失。 */
  compact?: boolean;
  /** 緊湊模式：清單中一次列多筆時用（確認訂單頁），縮圖與標籤收小、去掉外距，字級不變。 */
  dense?: boolean;
}) => {
  const isLight = theme === "light";
  return (
    <div style={{ margin: compact || dense ? "0" : "8px 0", transition: `margin ${SIZE_EASE}` }}>
      <XStack justifyContent="space-between" alignItems="center">
        <div
          style={{ display: "flex", alignItems: "center", flex: 1, gap: compact ? 8 : 16, transition: `gap ${SIZE_EASE}` }}>
          <YStack
            width={compact ? 34 : dense ? 48 : 64}
            height={compact ? 34 : dense ? 48 : 64}
            justifyContent="center"
            alignItems="center"
            flexShrink={0}
            borderRadius={compact ? 9 : 14}
            padding={compact ? 3 : 6}
            /* 淡霧頁首上不要用純白方塊：底本身就是近白的藍，白底只會浮成一張沒來由的小卡。
               改成同色系的淡藍托底（與帳戶字圖、身分膠囊同一種語彙）。 */
            backgroundColor={theme === "haze" ? v2.primarySoft : "#FEFEFE"}
            style={{ transition: `width ${SIZE_EASE}, height ${SIZE_EASE}, border-radius ${SIZE_EASE}, padding ${SIZE_EASE}` }}>
            <img
              src={kilogram != null ? getGasImageByKg(kilogram) : GasImg}
              alt="Gas"
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          </YStack>
          <YStack flex={1} width="70%">
            <ProName theme={theme} title={title} desc={desc} compact={compact} />
            {!compact && tags.length > 0 && (
              <XStack gap={dense ? 5 : 6} mt={dense ? 5 : 6} flexWrap="wrap">
                {tags
                  .filter((tag) => !!tag)
                  .map((tag, index) => (
                    <XStack
                      key={index}
                      backgroundColor={isLight ? "rgba(255, 255, 255, 0.2)" : colors.primaryActive}
                      px={dense ? 7 : 8}
                      py={2}
                      borderRadius={10}
                      borderWidth={isLight ? 1 : 0}
                      borderColor={isLight ? "rgba(255, 255, 255, 0.5)" : "transparent"}
                      ai="center">
                      <Text color={isLight ? "white" : colors.primary} fontSize={dense ? 12 : 14} fontWeight="700">
                        {tag}
                      </Text>
                    </XStack>
                  ))}
              </XStack>
            )}
          </YStack>
        </div>
        {!hidePrice && <ProPrice theme={theme} price={price} originalPrice={originalPrice} loading={priceLoading} compact={compact} />}
      </XStack>
    </div>
  );
};
